import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Swords } from "lucide-react";

const ACTIVITY_LABELS = { libero: "Libero", tecnico: "Tecnico", tattico: "Tattico", condizionato: "Condizionato", simulazione_gara: "Simulazione gara", altro: "Altro" };
const fmtDate = (d) => (d ? new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AthleteBoutsCard({ athleteId }) {
  const [bouts, setBouts] = useState(null);
  const [type, setType] = useState("all");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get(`/athletes/${athleteId}/bouts`).then((r) => setBouts(r.data || [])).catch(() => setBouts([]));
  }, [athleteId]);

  const types = useMemo(() => {
    const s = new Set((bouts || []).map((b) => b.activity_type || "altro"));
    return Object.keys(ACTIVITY_LABELS).filter((k) => s.has(k));
  }, [bouts]);

  const filtered = useMemo(() => (bouts || []).filter((b) => type === "all" || (b.activity_type || "altro") === type), [bouts, type]);
  const wins = filtered.filter((b) => b.result === "V").length;
  const losses = filtered.filter((b) => b.result === "D").length;
  const hitsFor = filtered.reduce((s, b) => s + (b.score_for || 0), 0);
  const hitsAgainst = filtered.reduce((s, b) => s + (b.score_against || 0), 0);
  const visible = showAll ? filtered : filtered.slice(0, 10);

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="athlete-bouts-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-heading font-semibold flex items-center gap-2"><Swords className="w-4 h-4" />Assalti</h2>
        <Link to="/scherma?tab=bout-quick" className="text-xs uppercase tracking-wider text-[#0052FF] hover:underline" data-testid="bouts-quick-link">Registra →</Link>
      </div>
      {bouts === null ? <div className="text-sm text-zinc-500">Caricamento…</div> : bouts.length === 0 ? (
        <div className="text-sm text-zinc-500" data-testid="bouts-empty">Nessun assalto registrato.</div>
      ) : (
        <>
          {types.length > 1 && (
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="bouts-type-filter">
              <FilterChip active={type === "all"} onClick={() => setType("all")} testid="bouts-type-all">Tutti · {bouts.length}</FilterChip>
              {types.map((t) => (
                <FilterChip key={t} active={type === t} onClick={() => setType(t)} testid={`bouts-type-${t}`}>
                  {ACTIVITY_LABELS[t]} · {bouts.filter((b) => (b.activity_type || "altro") === t).length}
                </FilterChip>
              ))}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Metric label="Assalti" value={filtered.length} testid="bouts-total" />
            <Metric label="V / D" value={<><span className="text-emerald-700">{wins}</span> <span className="text-zinc-300">/</span> <span className="text-red-600">{losses}</span></>} testid="bouts-vd" />
            <Metric label="Stoccate ± " value={`${hitsFor} · ${hitsAgainst}`} testid="bouts-hits" />
          </div>
          <div className="divide-y divide-zinc-100 border border-zinc-100 rounded" data-testid="bouts-list">
            {visible.map((b) => (
              <div key={b.canonical_id || b.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`bout-row-${b.canonical_id || b.id}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${b.result === "V" ? "bg-emerald-100 text-emerald-800" : b.result === "D" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-600"}`}>{b.result || "="}</span>
                <span className="font-mono font-semibold w-14 shrink-0">{b.score_for}–{b.score_against}</span>
                <span className="flex-1 min-w-0 truncate">
                  {b.opponent_athlete_id ? <Link to={`/atleti/${b.opponent_athlete_id}`} className="hover:underline">{b.opponent || "Atleta CSP"}</Link> : <span>{b.opponent || "—"}</span>}
                  {!b.opponent_athlete_id && <span className="text-[10px] text-zinc-400 ml-1 uppercase">esterno</span>}
                </span>
                <span className="text-[11px] text-zinc-500 shrink-0 hidden sm:inline">{ACTIVITY_LABELS[b.activity_type] || b.bout_type || "—"}</span>
                <span className="text-xs text-zinc-500 font-mono shrink-0">{fmtDate(b.date)}</span>
              </div>
            ))}
          </div>
          {filtered.length > 10 && (
            <button data-testid="bouts-toggle-all" onClick={() => setShowAll(!showAll)} className="mt-2 text-xs uppercase tracking-wider text-[#0052FF] hover:underline">
              {showAll ? "Mostra meno" : `Mostra tutti (${filtered.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-heading text-2xl font-bold" data-testid={testid}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={`text-xs px-2.5 py-1 rounded-full border transition ${active ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}>{children}</button>
  );
}
